import React, {useState} from 'react'
import {Speaker} from 'Event/Speakers'
import SettingsPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/SettingsPanel'
import {REMOVE, useSaveTemplate} from 'Event/TemplateUpdateProvider'
import Config from 'organization/Event/Configurable/Config'
import RulesPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/RulesPanel'
import {useConfig} from 'organization/Event/Configurable'
import {Controller} from 'react-hook-form'
import {EnabledSwitch} from 'lib/ui/form/Switch'
import {onChangeCheckedHandler} from 'lib/dom'
import TextField from 'lib/ui/TextField'
import HTMLEditor, {HTMLEditorContainer} from 'lib/ui/form/HTMLEditor'
import ImageAssetUploader from 'lib/asset/ImageAssetUploader'
import Button from 'lib/ui/Button'
import {generateHashId} from 'lib/crypto/hash'
import {useLeftyTemplate} from 'Event/template/Lefty'
import styled from 'styled-components'
import {useAddTranslation, useRemoveTranslations} from 'Event/LanguageProvider'
import {useAttendeeVariables} from 'Event'

export default function SpeakerConfig(props: {speaker: Speaker; id: string}) {
  const {featuredSpeakers} = useLeftyTemplate()
  const {speaker, id} = props
  const [rules, setRules] = useState(speaker.rules)
  const {toggle} = useConfig()
  const addTranslation = useAddTranslation()
  const removeTranslations = useRemoveTranslations()

  const set = useSaveTemplate()

  const handleSave = (data: any) => {
    const {name, text} = data.speakers.items[id]

    const saveData = {
      localization: addTranslation({
        [`speakers_items_${id}_name`]: name || 'Speaker',
        [`speakers_items_${id}_text`]: text || 'Speaker bio',
      }),
      speakers: {
        ...data.speakers,
        items: {
          [id]: {
            ...data.speakers.items[id],
            name: `{{speakers_items_${id}_name}}`,
            text: `{{speakers_items_${id}_text}}`,
            rules,
          },
        },
      },
    }

    set(saveData)
  }

  const handleRemove = () => {
    set({
      localization: removeTranslations([
        `{{speakers_items_${id}_name}}`,
        `{{speakers_items_${id}_text}}`,
      ]),
      speakers: {
        items: {
          [props.id]: REMOVE,
        },
      },
    })
  }

  const handleMoveToFeaturedSpeaker = () => {
    const numSpeakers = Object.keys(featuredSpeakers.items).length
    const position = numSpeakers + 1

    generateHashId([
      'lefty_',
      'speaker',
      position.toString(),
      new Date().valueOf().toString(),
      Math.random().toString(),
    ]).then((id) => {
      set({
        speakers: {
          items: {
            [props.id]: REMOVE,
          },
        },
        featuredSpeakers: {
          items: {
            [id]: {
              ...speaker,
              position,
            },
          },
        },
      })
      toggle()
    })
  }

  return (
    <Config title="Speaker Config" onSave={handleSave} onRemove={handleRemove}>
      <SettingsPanel>
        <Settings
          {...props}
          onMoveToFeaturedSpeaker={handleMoveToFeaturedSpeaker}
        />
      </SettingsPanel>
      <RulesPanel rules={rules} setRules={setRules} />
    </Config>
  )
}

function Settings(props: {
  speaker: Speaker
  id: string
  onMoveToFeaturedSpeaker: () => void
}) {
  const {speaker, id, onMoveToFeaturedSpeaker} = props
  const {
    form: {control, register},
  } = useConfig()
  const v = useAttendeeVariables()
  return (
    <>
      <Controller
        name={`speakers.items.${id}.isActive`}
        control={control}
        defaultValue={speaker.isActive ?? true}
        render={({onChange, value}) => (
          <EnabledSwitch
            checked={value}
            onChange={onChangeCheckedHandler(onChange)}
          />
        )}
      />
      <TextField
        name={`speakers.items.${id}.name`}
        label="Speaker Name"
        required
        fullWidth
        inputProps={{
          ref: register,
          'aria-label': 'set speaker name',
        }}
        defaultValue={v(speaker.name)}
      />

      <Controller
        name={`speakers.items.${id}.text`}
        defaultValue={v(speaker.text || '')}
        control={control}
        render={({onChange, value}) => (
          <StyledHTMLEditorContainer>
            <HTMLEditor data={value} onChange={onChange} />
          </StyledHTMLEditorContainer>
        )}
      />

      <Controller
        name={`speakers.items.${id}.image`}
        control={control}
        defaultValue={speaker.image}
        render={({onChange, value}) => (
          <ImageAssetUploader
            onChange={onChange}
            value={value}
            uploadLabel="Image"
            uploadInputProps={{
              'aria-label': 'speaker image upload',
            }}
            width={300}
            height={300}
            canResize
            disableAutoRemove
          />
        )}
      />
      <Button
        variant="contained"
        color="primary"
        onClick={onMoveToFeaturedSpeaker}
      >
        Move to Featured Speakers
      </Button>
    </>
  )
}

const StyledHTMLEditorContainer = styled(HTMLEditorContainer)`
  overflow-x: visible !important;
`
